import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  InlineMessage,
  LoadingState,
} from '../components';
import { ExportStatusBadge } from '../features/exports/ExportStatusBadge';
import { describeExportError, formatFileSize, formatInteger } from '../features/exports/messages';
import { formatDateTime } from '../features/results/ResultCard';
import { useExportList } from '../hooks/use-exports';
import { downloadExport } from '../services/download';
import { describeError, messageForCode } from '../services/errors';
import type { ExportSummary } from '../types/api';

/**
 * Histórico de exportações (RF-EU13).
 *
 * Existe porque o pedido sobrevive ao diálogo: o registro vive no banco, então
 * fechar a janela, navegar ou recarregar não pode significar perder o arquivo.
 */
export function ExportsPage() {
  const exports = useExportList(20);
  const [failedDownload, setFailedDownload] = useState<{ id: string; message: string } | null>(
    null,
  );
  const items = exports.data?.pages.flatMap((page) => page.data) ?? [];

  const download = (exportId: string): void => {
    setFailedDownload(null);
    downloadExport(exportId).catch((error: unknown) =>
      setFailedDownload({ id: exportId, message: describeExportError(error) }),
    );
  };

  return (
    <Card>
      <CardHeader
        title="Exportações"
        description="Arquivos gerados a partir dos seus resultados, do mais recente para o mais antigo."
      />

      {exports.isPending && <LoadingState label="Carregando exportações" />}

      {exports.isError && (
        <ErrorState message={describeError(exports.error)} onRetry={() => void exports.refetch()} />
      )}

      {!exports.isPending && !exports.isError && items.length === 0 && (
        <EmptyState
          title="Nenhuma exportação ainda"
          description="Abra uma pesquisa, aplique os filtros que quiser e use o botão Exportar na lista de resultados."
        />
      )}

      {items.length > 0 && (
        <>
          <ul className="flex flex-col divide-y divide-border">
            {items.map((item) => (
              <li key={item.id} className="flex flex-col gap-2 py-3">
                <ExportRow
                  item={item}
                  onDownload={() => download(item.id)}
                  error={failedDownload?.id === item.id ? failedDownload.message : null}
                />
              </li>
            ))}
          </ul>

          {exports.hasNextPage && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="secondary"
                loading={exports.isFetchingNextPage}
                onClick={() => void exports.fetchNextPage()}
              >
                Carregar mais
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function ExportRow({
  item,
  onDownload,
  error,
}: {
  item: ExportSummary;
  onDownload: () => void;
  error: string | null;
}) {
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <ExportStatusBadge status={item.status} />
        <Badge>{item.format.toUpperCase()}</Badge>
        <span className="text-sm text-text">
          {item.rowCount === null
            ? 'Contagem ainda não disponível'
            : `${formatInteger(item.rowCount)} resultado(s)`}
        </span>

        <div className="ml-auto flex flex-wrap items-center gap-3">
          {item.status === 'COMPLETED' && (
            <Button size="sm" onClick={onDownload}>
              Baixar
            </Button>
          )}
          {/* Sem o recorte gravado exposto na API, "gerar de novo" só pode ser
              feito de onde os filtros existem: a própria pesquisa (§17). */}
          <Link
            to={`/pesquisas/${item.searchId}`}
            className="text-sm font-medium text-accent hover:underline"
          >
            Abrir pesquisa
          </Link>
        </div>
      </div>

      <p className="text-xs text-text-muted">
        Criada em {formatDateTime(item.createdAt)}
        {item.status === 'COMPLETED' && ` · ${formatFileSize(item.fileSizeBytes)}`}
        {item.status === 'COMPLETED' &&
          item.expiresAt &&
          ` · disponível até ${formatDateTime(item.expiresAt)}`}
      </p>

      {item.status === 'EXPIRED' && (
        <InlineMessage tone="warning">
          O arquivo expirou e foi removido. Abra a pesquisa e exporte novamente.
        </InlineMessage>
      )}

      {item.status === 'FAILED' && (
        <InlineMessage tone="danger">
          {item.errorCode ? messageForCode(item.errorCode) : 'Não foi possível gerar a exportação.'}
        </InlineMessage>
      )}

      {error && <InlineMessage tone="danger">{error}</InlineMessage>}
    </>
  );
}
